import React, { createContext, useCallback, useContext } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { propertyNavigateOptions, propertyRoutes, Routes } from '@/routes.ts';
import type { NavigateOptions } from '@tanstack/router-core';
import { useHouseContext } from '@/context/HouseContext';

interface AppNavigationContextType {
	navigateToRoute: (options?: NavigateOptions) => Promise<void>;
	navigateToDashboard: (options?: NavigateOptions) => Promise<void>;
	navigateToProperty: (publicId: string, options?: NavigateOptions) => Promise<void>;
	navigateToAddNewProperty: () => Promise<void>;
	navigateToMyBundles: () => Promise<void>;
	navigateToPropertySetup: () => Promise<void>;
	navigateToSetupProperty: () => Promise<void>;
}

const AppNavigationContext = createContext<AppNavigationContextType | null>(null);

/**
 * Hook to access the dashboard navigation context
 *
 * Provides access to navigation functions and assistant reset trigger
 */
export const useAppNavigation = () => {
	const context = useContext(AppNavigationContext);
	if (!context) {
		throw new Error('useAppNavigation must be used within a AppNavigationProvider');
	}
	return context;
};

/**
 * Provider component for the dashboard navigation context
 *
 * Manages navigation state and provides navigation functions
 */
export const AppNavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const navigate = useNavigate();
	const { selectedHouse } = useHouseContext();

	const navigateToRoute = useCallback(
		async (options?: NavigateOptions) => {
			return await navigate({ to: '/', ...(options ?? {}) });
		},
		[navigate],
	);

	/**
	 * Navigates to the property dashboard using the public_id from context.
	 * Falls back to /properties if no house is selected yet.
	 */
	const navigateToDashboard = useCallback(
		async (options?: NavigateOptions) => {
			const to = selectedHouse?.public_id ? propertyRoutes(selectedHouse.public_id).dashboard : Routes.PROPERTIES;
			// @ts-expect-error - dynamic path string not in router's type union
			return await navigate({ to, ...(options ?? {}) });
		},
		[navigate, selectedHouse],
	);

	/**
	 * Navigates to a specific property's dashboard by its public_id. Unlike navigateToDashboard, the
	 * target is passed in rather than read from context, so callers that have just changed the
	 * selection (e.g. claiming a transfer) don't race the stale selectedHouse in this closure.
	 */
	const navigateToProperty = useCallback(
		async (publicId: string, options?: NavigateOptions) => {
			return await navigate({ ...propertyNavigateOptions(publicId), ...(options ?? {}) });
		},
		[navigate],
	);

	const navigateToPropertySetup = useCallback(
		async (options?: NavigateOptions) => {
			return await navigate({ to: Routes.HOUSE_SETUP, ...(options ?? {}) });
		},
		[navigate],
	);

	const navigateToAddNewProperty = useCallback(async () => {
		return await navigate({ to: Routes.HOUSE_SETUP_NEW });
	}, [navigate]);

	const navigateToMyBundles = useCallback(async () => {
		return await navigate({ to: Routes.PROPERTIES });
	}, [navigate]);

	const navigateToSetupProperty = useCallback(async () => {
		return await navigate({ to: Routes.HOUSE_SETUP });
	}, [navigate]);

	const value = {
		navigateToRoute,
		navigateToDashboard,
		navigateToProperty,
		navigateToAddNewProperty,
		navigateToMyBundles,
		navigateToPropertySetup,
		navigateToSetupProperty,
	};
	return <AppNavigationContext.Provider value={value}>{children}</AppNavigationContext.Provider>;
};
